/**
 * pauseDateRules — les bornes des deux calendriers et la phrase qui refuse une fenêtre.
 *
 * Port littéral des dérivations de `ParentPauseSubscriptionModal.js` : `cockpitWindowStart` /
 * `cockpitWindowEve` / `windowStart` (`:66-82`), `minStart` / `maxStart` (`:85-103`),
 * `summerResumeCap` (`:110-113`), `duringSummerWindow` (`:121-130`), les trois refus de durée
 * (`:163-171`, affichés `:512-540`) et `seasonCrossingMessage` (`:174-198`).
 *
 * Les bornes prennent `now` en argument : elles sont datées, elles se testent temps figé. Le refus,
 * lui, ne lit que la fenêtre posée — jamais la date du jour.
 */
import moment from "moment"

import { type ApiSummerConfig, getSummerConfigSafe, getSummerJuneBounds } from "../Subscription/summerGates"
import { type PauseRules } from "./pauseBalanceAdapter"

/**
 * Le legacy plafonne la date de début avec la durée minimale PAR DÉFAUT (5 j) et non avec celle
 * du solde : `maxStart` se calcule avant que `getParentPauseBalance` ait répondu (`:96-99`).
 */
const DEFAULT_MIN_DURATION_DAYS = 5

/** Les frontières d'été que la pause manuelle ne franchit pas. */
type SummerFrame = {
    /** 1ᵉʳ juin — premier jour géré par le cockpit. */
    cockpitWindowStart: moment.Moment
    /** 31 mai — dernier jour où une pause manuelle peut reprendre avant le cockpit. */
    cockpitWindowEve: moment.Moment
    /** 1ᵉʳ juillet — ouverture de la fenêtre estivale. */
    windowStart: moment.Moment
    /** 31 août — sa fermeture. */
    windowEnd: moment.Moment
}

function summerFrame(summerConfig: ApiSummerConfig | null | undefined): SummerFrame {
    const cockpitWindowStart = getSummerJuneBounds(summerConfig).juneStart.startOf("day")
    return {
        cockpitWindowStart,
        cockpitWindowEve: cockpitWindowStart.clone().subtract(1, "day"),
        windowStart: cockpitWindowStart.clone().add(1, "month"),
        windowEnd: moment(getSummerConfigSafe(summerConfig).windowEnd).endOf("day"),
    }
}

export type PauseWindowArgs = {
    now: moment.Moment
    summerConfig?: ApiSummerConfig | null
    /** `user.has_summer_fast_track` — ces familles ne passent pas par le cockpit (`:88-90`). */
    hasSummerFastTrack?: boolean
    rules: PauseRules
    /** La date de début déjà choisie : elle décide des bornes de la reprise. */
    startDate?: string | null
}

export type PauseWindow = {
    startMinDate: Date
    startMaxDate: Date
    resumeMinDate: Date
    /** Absente tant qu'aucune date de début n'est posée — comme le `validRange` du legacy. */
    resumeMaxDate?: Date
    /** Pendant la fenêtre estivale, le backend refuse toute pause manuelle : on n'ouvre pas. */
    startDateDisabled: boolean
}

export function pauseDateWindow(args: PauseWindowArgs): PauseWindow {
    const { now, summerConfig, hasSummerFastTrack = false, rules, startDate } = args
    const { cockpitWindowEve, windowStart, windowEnd } = summerFrame(summerConfig)

    const today = now.clone().startOf("day")
    const { start: minStart } = earliestStart(today, rules)
    const beforeSummerWindow = !hasSummerFastTrack && today.isBefore(windowStart)

    const defaultMaxStart = today.clone().add(6, "months")
    const startCap = cockpitWindowEve.clone().subtract(DEFAULT_MIN_DURATION_DAYS, "days")
    const maxStart = beforeSummerWindow && startCap.isBefore(defaultMaxStart) ? startCap : defaultMaxStart

    const summerResumeCap = beforeSummerWindow ? cockpitWindowEve.clone() : null

    const start = startDate ? moment(startDate) : null
    const resumeMin = (start ?? minStart).clone().add(rules.minDurationDays, "days")

    let resumeMax: moment.Moment | undefined
    if (start) {
        const base = start.clone().add(longestPauseDays(rules), "days")
        resumeMax = summerResumeCap && summerResumeCap.isBefore(base) ? summerResumeCap : base
    }

    return {
        startMinDate: minStart.toDate(),
        startMaxDate: maxStart.toDate(),
        resumeMinDate: resumeMin.toDate(),
        resumeMaxDate: resumeMax?.toDate(),
        startDateDisabled: !hasSummerFastTrack && today.isSameOrAfter(windowStart) && today.isSameOrBefore(windowEnd),
    }
}

/** La plus longue pause que l'app laisse poser : sa limite, ou ce qui reste du quota s'il est plus court. */
function longestPauseDays(rules: PauseRules): number {
    return Math.min(rules.maxConsecutiveDays, rules.remainingDays ?? rules.maxConsecutiveDays)
}

/** La reprise est au plus loin que l'app permet : au-delà, la pause passe par l'équipe. */
export const PAUSE_MAX_DURATION_NOTE = "Pour une pause plus longue, écrivez-nous."
/** La reprise dépasse la limite (le début a été avancé après coup) : le geste qui la remet dedans. */
export const PAUSE_TOO_LONG_ERROR = "Choisissez une date de reprise plus proche."
/** La dernière pause de la famille vient de se terminer : la suivante attend, ou passe par l'équipe. */
export const PAUSE_WAIT_NOTE = "Une nouvelle pause n'est pas encore possible depuis l'application."

/** Demain, ou plus tard (`waits`) quand la dernière pause de la famille vient de se terminer. */
function earliestStart(today: moment.Moment, rules: PauseRules): { start: moment.Moment; waits: boolean } {
    const tomorrow = today.clone().add(1, "day")
    const afterLastPause = rules.nextPauseMinStart ? moment(rules.nextPauseMinStart).startOf("day") : null
    return afterLastPause?.isAfter(tomorrow)
        ? { start: afterLastPause, waits: true }
        : { start: tomorrow, waits: false }
}

/**
 * La phrase courte quand le parent atteint une limite de l'app, ou `null` : la reprise au plus
 * loin, ou une nouvelle pause qui attend. Ni nombre de jours, ni date, ni calcul (Wajih, 14/10) :
 * les calendriers tiennent la règle, la phrase dit seulement qu'au-delà on écrit à l'équipe.
 */
export function pauseLimitNote(args: { now: moment.Moment; rules: PauseRules; durationDays: number }): string | null {
    const { now, rules, durationDays } = args
    // Le quota restant arrête le calendrier comme la limite : l'équipe peut aller au-delà des deux.
    if (durationDays > 0 && durationDays >= longestPauseDays(rules)) return PAUSE_MAX_DURATION_NOTE
    return earliestStart(now.clone().startOf("day"), rules).waits ? PAUSE_WAIT_NOTE : null
}

/** Entrée en saison (1ᵉʳ juillet) — possédée par le Programme d'été (`seasonBoundary.js:13`). */
export const CROSSING_SEASON_START = "season_start"
/** Sortie de saison — possédée par la page Abonnement (`seasonBoundary.js:16`). */
export const CROSSING_SEASON_END = "season_end"

export type SeasonCrossing = typeof CROSSING_SEASON_START | typeof CROSSING_SEASON_END | null

/**
 * La borne de SORTIE de saison d'une famille : le lendemain du dernier jour couvert. Port
 * littéral de `getSeasonalStopFromActivation` (`seasonBoundary.js:68-77`).
 */
export function seasonalStopFromActivation(upcomingActivation: unknown): moment.Moment | null {
    const activation = upcomingActivation as { type?: string; due_date?: string } | null | undefined
    const type = activation?.type
    if (type !== "cours_ete" && type !== "stage") return null
    if (!activation?.due_date) return null
    return moment(activation.due_date).add(1, "day").startOf("day")
}

function crosses(boundary: moment.Moment | null, start: moment.Moment, resume: moment.Moment): boolean {
    if (!boundary?.isValid()) return false
    const day = boundary.clone().startOf("day")
    return !start.isAfter(day, "day") && !resume.isBefore(day, "day")
}

/**
 * Quelle frontière de saison la fenêtre [start, resume] enjambe-t-elle ? Port littéral de
 * `getSeasonBoundaryCrossed` (`seasonBoundary.js:48-65`) — bornes INCLUSES, et la frontière la
 * plus précoce l'emporte quand la fenêtre les enjambe toutes les deux.
 *
 * ⚠️ Exprimé sur la FENÊTRE POSÉE, jamais sur la date du jour : c'est justement la famille que
 * les gardes d'été exemptent — arrivée par le funnel Cours d'été — qui pouvait poser 15/08 → 15/09.
 */
export function seasonBoundaryCrossed(args: {
    seasonStart?: moment.Moment | null
    seasonalStopOn?: moment.Moment | null
    start?: string | null
    resume?: string | null
}): SeasonCrossing {
    const from = args.start ? moment(args.start) : null
    const to = args.resume ? moment(args.resume) : null
    if (!from?.isValid() || !to?.isValid()) return null

    const candidates: Array<[SeasonCrossing, moment.Moment]> = (
        [
            [CROSSING_SEASON_START, args.seasonStart ?? null],
            [CROSSING_SEASON_END, args.seasonalStopOn ?? null],
        ] as Array<[SeasonCrossing, moment.Moment | null]>
    )
        .filter((entry): entry is [SeasonCrossing, moment.Moment] => crosses(entry[1], from, to))
        .sort((a, b) => a[1].valueOf() - b[1].valueOf())

    return candidates.length > 0 ? candidates[0][0] : null
}

export type PauseDateErrorArgs = {
    summerConfig?: ApiSummerConfig | null
    rules: PauseRules
    startDate?: string | null
    resumeDate?: string | null
    durationDays: number
    /** `user.upcoming_activation` — porte la borne de sortie de saison des produits d'appel. */
    upcomingActivation?: unknown
}

/**
 * La phrase qui refuse les dates choisies, ou `null`.
 *
 * Le legacy peut en afficher DEUX à la fois (un refus de durée sous les champs, le refus de
 * saison sous les motifs) ; le gabarit du design system n'en porte qu'une. L'ordre est celui du
 * rendu legacy : durée minimale, durée maximale, quota, puis frontière de saison.
 */
export function pauseDateError(args: PauseDateErrorArgs): string | null {
    const { summerConfig, rules, startDate, resumeDate, durationDays, upcomingActivation } = args

    if (startDate && resumeDate && durationDays < rules.minDurationDays) {
        return `La pause doit durer au moins ${rules.minDurationDays} jours.`
    }
    // La phrase de limite, avec « Nous écrire », dit déjà le chemin au-delà.
    if (durationDays > rules.maxConsecutiveDays) {
        return PAUSE_TOO_LONG_ERROR
    }
    if (durationDays > 0 && typeof rules.remainingDays === "number" && durationDays > rules.remainingDays) {
        return `Il vous reste ${rules.remainingDays} jours sur votre quota annuel.`
    }

    const { windowStart } = summerFrame(summerConfig)
    const crossing = seasonBoundaryCrossed({
        seasonStart: windowStart,
        seasonalStopOn: seasonalStopFromActivation(upcomingActivation),
        start: startDate,
        resume: resumeDate,
    })

    // Le refus nomme l'écran compétent plutôt qu'une impossibilité.
    if (crossing === CROSSING_SEASON_START) {
        return `Une pause ne peut pas s'étendre au-delà du ${windowStart.format("DD/MM/YYYY")} : juillet et août s'organisent depuis le Programme d'été.`
    }
    if (crossing === CROSSING_SEASON_END) {
        return "Une pause ne peut pas s'étendre au-delà de la fin de vos Cours d'été : la reprise se planifie depuis « Mon abonnement », section « Et ensuite ? »."
    }

    return null
}
